import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, DownloadSimple, MagnifyingGlass, X, Paperclip } from '@phosphor-icons/react';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { fmtStamp } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { key: 'nova', label: 'Nova', cls: 'bg-slate-100 text-slate-600' },
  { key: 'selecionada', label: 'Selecionada', cls: 'bg-brand-green/10 text-brand-green' },
  { key: 'lista_espera', label: 'Lista de espera', cls: 'bg-brand-gold/20 text-brand-gold-hover' },
  { key: 'recusada', label: 'Recusada', cls: 'bg-red-50 text-red-500' },
];
const statusMeta = (k) => STATUS_OPTIONS.find(s => s.key === k) || STATUS_OPTIONS[0];

function ResponseDetail({ response, onClose, onUpdate }) {
  const [notes, setNotes] = useState(response.internal_notes || '');
  const [status, setStatus] = useState(response.status);

  const save = async () => {
    try {
      const { data } = await axios.put(`${API}/api/forms/${response.form_id}/responses/${response.id}`, { status, internal_notes: notes });
      onUpdate(data);
      toast.success('Guardado');
    } catch { toast.error('Não foi possível guardar'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="response-detail-modal">
        <div className="flex justify-between items-center mb-4">
          <p className="font-medium text-brand-text">Resposta de {fmtStamp(response.submitted_at)}</p>
          <button onClick={onClose} data-testid="close-response-detail-btn"><X size={18} className="text-brand-muted" /></button>
        </div>
        <div className="space-y-3 mb-4">
          {response.answers.map(a => (
            <div key={a.field_id}>
              <p className="text-[11px] uppercase tracking-wide text-brand-muted font-semibold">{a.label}</p>
              {a.type === 'file_upload' ? (
                <a href={`${API}/api/files/${a.value}`} target="_blank" rel="noreferrer" className="text-sm text-brand-green flex items-center gap-1 hover:underline" data-testid={`response-file-link-${a.field_id}`}>
                  <Paperclip size={13} /> Ver ficheiro
                </a>
              ) : (
                <p className="text-sm text-brand-text">{Array.isArray(a.value) ? a.value.join(', ') : String(a.value)}</p>
              )}
            </div>
          ))}
        </div>
        <div className="mb-3">
          <label className="text-xs text-brand-muted block mb-1.5">Estado</label>
          <div className="flex gap-1.5 flex-wrap">
            {STATUS_OPTIONS.map(s => (
              <button key={s.key} onClick={() => setStatus(s.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${status === s.key ? 'ring-2 ring-brand-green' : ''} ${s.cls}`} data-testid={`response-status-btn-${s.key}`}>{s.label}</button>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs text-brand-muted block mb-1.5">Notas internas</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg border border-brand-border text-sm" data-testid="response-notes-input" />
        </div>
        <button onClick={save} className="w-full py-2.5 rounded-full bg-brand-green text-white text-sm font-medium" data-testid="save-response-btn">Guardar</button>
      </div>
    </div>
  );
}

export default function AdminFormResponsesPage() {
  useRequireAdmin();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(() => {
    axios.get(`${API}/api/forms/${id}`).then(({ data }) => setForm(data)).catch(() => { toast.error('Formulário não encontrado'); navigate('/admin/formularios'); });
    axios.get(`${API}/api/forms/${id}/responses`, { params: { status: statusFilter, search } }).then(({ data }) => setResponses(data)).finally(() => setLoading(false));
  }, [id, statusFilter, search, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const exportCsv = () => { window.open(`${API}/api/forms/${id}/responses/export`, '_blank'); };

  const summaryLabel = (r) => {
    const a = r.answers.find(x => x.type === 'short_text' || x.type === 'email') || r.answers[0];
    return a ? String(Array.isArray(a.value) ? a.value.join(', ') : a.value) : '(sem resposta)';
  };

  return (
    <div className="space-y-5" data-testid="admin-form-responses-page">
      <button onClick={() => navigate(`/admin/formularios/${id}`)} className="flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-green" data-testid="back-to-builder-btn">
        <ArrowLeft size={16} /> Voltar ao formulário
      </button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl text-brand-green font-semibold">Respostas {form ? `- ${form.title}` : ''}</h1>
        <button onClick={exportCsv} className="px-4 py-2 rounded-full border border-brand-border text-sm text-brand-text flex items-center gap-1.5 hover:bg-brand-bg" data-testid="export-csv-btn">
          <DownloadSimple size={15} /> Exportar CSV
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-placeholder" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar respostas..." className="w-full pl-9 pr-3 py-2 rounded-xl border border-brand-border text-sm" data-testid="response-search-input" />
        </div>
        <button onClick={() => setStatusFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${!statusFilter ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`} data-testid="filter-status-all">Todos</button>
        {STATUS_OPTIONS.map(s => (
          <button key={s.key} onClick={() => setStatusFilter(s.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === s.key ? 'ring-2 ring-brand-green' : ''} ${s.cls}`} data-testid={`filter-status-${s.key}`}>{s.label}</button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-brand-muted">A carregar...</p>
      ) : responses.length === 0 ? (
        <div className="bg-white rounded-2xl border border-brand-border p-10 text-center">
          <p className="text-sm text-brand-muted">Ainda não há respostas para mostrar.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {responses.map(r => (
            <button key={r.id} onClick={() => setSelected(r)} className="w-full bg-white rounded-xl border border-brand-border p-3.5 flex items-center gap-3 text-left hover:border-brand-green transition-colors" data-testid={`response-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-brand-text font-medium truncate">{summaryLabel(r)}</p>
                <p className="text-xs text-brand-muted">{fmtStamp(r.submitted_at)}</p>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-1 rounded-full flex-shrink-0 ${statusMeta(r.status).cls}`}>{statusMeta(r.status).label}</span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <ResponseDetail response={selected} onClose={() => setSelected(null)} onUpdate={(updated) => { setResponses(prev => prev.map(r => r.id === updated.id ? updated : r)); setSelected(updated); }} />
      )}
    </div>
  );
}
